import history from 'common/services/HistoryService';
import urlUtil, { getUrlParam } from 'common/util/url/urlUtil';

import urlService from 'common/services/api/urlService';
import store from '../store';
import { RootState } from '../store/rootReducer';
import { ContextUrlSearchStringObject } from '../store/context/types';
import { setupWorkspaces } from '../store/context/actions';

const routingService = {
    getNewContextFromLocation(location: { search: string }): ContextUrlSearchStringObject {
        return urlUtil.parseLocationSearchString(location.search) as ContextUrlSearchStringObject;
    },

    getUrlRelevantPropsFromContext() {
        const state: RootState = store.getState() as RootState;
        const {
            assessmentRunId, partyId, questionId, workspaceId,
        } = state.context;

        return {
            assessmentRunId, partyId, questionId, workspaceId,
        };
    },

    getBaseUrlFromFullGradingUrl(fullUrl: string): string {
        if (fullUrl && fullUrl.indexOf('/grading') > 0) {
            return fullUrl.substring(0, fullUrl.indexOf('/grading'));
        }
        return undefined;
    },
    getPathFromFullGradingUrl(fullUrl: string): string {
        if (fullUrl && fullUrl.indexOf('/grading') > 0) {
            // returns whatever is after /grading in a url, as this is used in getNewContextFromRoute
            return fullUrl.substr(fullUrl.indexOf('/grading') + 8);
        }
        return undefined;
    },

    getStorageKeyForLastVisitedUrl(): string {
        const { session, context } = store.getState() as RootState;

        return `insperaGradingLastVisited_${session.user.id}_${context.assessmentRunId}`;
    },

    getStorageKeyForWorkflowCommitteeInitCheck(): string {
        const { session, context } = store.getState() as RootState;

        return `insperaGradingWorkflowCommitteeInit_${session.user.id}_${context.assessmentRunId}`;
    },

    hasCheckedWorkflowCommitteeOnInitialLoad(): boolean {
        const storageKey = this.getStorageKeyForWorkflowCommitteeInitCheck();
        return localStorage.getItem(storageKey) === 'true';
    },

    markWorkflowCommitteeCheckedOnInitialLoad(): void {
        const storageKey = this.getStorageKeyForWorkflowCommitteeInitCheck();
        localStorage.setItem(storageKey, 'true');
    },

    clearWorkflowCommitteeCheckedOnInitialLoad(): void {
        const storageKey = this.getStorageKeyForWorkflowCommitteeInitCheck();
        localStorage.removeItem(storageKey);
    },

    navigateToLastVisitedUrl(): void {
        const storageKey = this.getStorageKeyForLastVisitedUrl();
        let insperaGradingLastVisited = localStorage.getItem(storageKey);
        const contextFromLocation = routingService.getNewContextFromLocation(window.location);
        const printMode: string = getUrlParam('printMode');
        const state: RootState = store.getState() as RootState;
        const { assessment } = state;

        // if going from a non committee context to a committee context or the other way around
        // or grading workflows are enabled for the test, reset last visited
        if (insperaGradingLastVisited) {
            const contextFromSavedLocation = urlUtil.parseLocationSearchString(insperaGradingLastVisited) as ContextUrlSearchStringObject;
            if ((contextFromSavedLocation.selectedCommitteeId === 0 && contextFromLocation.selectedCommitteeId !== 0)
                || (contextFromSavedLocation.selectedCommitteeId !== 0 && contextFromLocation.selectedCommitteeId === 0)
                || (assessment?.isGradingWorkflowsEnabledForTest)) {
                insperaGradingLastVisited = null;
                localStorage.removeItem(storageKey);
            }
        }

        // open last visit url if there is a record in the local storage and has no workspaceId passed info the address bar
        if (insperaGradingLastVisited && !contextFromLocation.workspaceId) {
            history.replace(insperaGradingLastVisited);
        }

        // load first workspace if no last visit url stored and no workspaceId passed into the address bar
        if (!insperaGradingLastVisited && !contextFromLocation.workspaceId) {
            store.dispatch(setupWorkspaces());
        }

        /* first open a url with workspaceId and has no last visited url in localStorage,
         we need this due to userInfo object load after context, and grading is not loaded in an iframe and
         also not in  printMode */
        if (contextFromLocation.workspaceId && window === window.parent && !printMode) {
            localStorage.setItem(storageKey, `/${window.location.search}`);
        }
    },

    redirectToAdminPortal(): void {
        window.location.replace(urlService.prependDomain('/adminportal'));
    },
};

export default routingService;
